import { useEffect, useState } from "react";
import { SaveTypeCatalog } from "./TypeCatalogService";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useIndexLogic } from "./index-logic";
import { useInventoryPermission } from "../permissions/InventoryPermissions";

export const useAddAssetTypeLogic = ({ handleClose, setLoading, open, selectedAssetType }) => {
  //#region Permissions
  const { InventoryPermissionProps } = useInventoryPermission();
  const {
    hasTypeCatalogSavePermission,
  } = InventoryPermissionProps;
  //#endregion Permissions

  const { IndexProps } = useIndexLogic();
  const { loadData } = IndexProps;
  // Form state
  const [assetTypeForm, setAssetTypeForm] = useState({
    typeCatalogID: 0,
    entityType: "Asset",
    typeName: "",
    description: "",
    name: "", // The API schema includes this but it might be redundant with typeName
    isVehicle: false,
    isActive: true
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (selectedAssetType) {
        setAssetTypeForm({
          ...selectedAssetType
        });
      } else {
        // Reset for a new asset type
        resetForm();
      }
    }
  }, [open, selectedAssetType]);

  // Form change handlers
  const handleInputChange = (e) => {
    
    if(!hasTypeCatalogSavePermission) return;

    const { name, value, checked } = e.target;
    setAssetTypeForm({
      ...assetTypeForm,
      [name]: name === 'isVehicle' ? checked : value
    });
    
    // Clear error for this field if any
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!assetTypeForm.typeName.trim()) {
      newErrors.typeName = "Asset Type name is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save asset type
  const handleSaveAssetType = async () => {
    if(!hasTypeCatalogSavePermission) return { success: false };

    try {
      if (!validateForm()) {
        return { success: false };
      }
      
      const formData = {
        ...assetTypeForm,
        name: assetTypeForm.name || assetTypeForm.typeName
      };
      
      const response = await SaveTypeCatalog(formData);
      
      if (!response.isError) {
        // Prepare the created item data - use form data FIRST, then spread response
        const createdItem = {
          typeCatalogID: response.result?.typeCatalogID || assetTypeForm.typeCatalogID,
          typeName: assetTypeForm.typeName, // Keep the form value
          typeCategory: "Asset",
          description: assetTypeForm.description || "",
          isVehicle: assetTypeForm.isVehicle || false,
          isActive: assetTypeForm.isActive,
          entityType: assetTypeForm.entityType || "Asset",
          name: assetTypeForm.name || assetTypeForm.typeName,
          // Spread response result AFTER to override with API data if present
          ...(response.result || {}),
          // But keep these from form to ensure they're not null
          typeName: response.result?.typeName || assetTypeForm.typeName,
          description: response.result?.description || assetTypeForm.description || "",
        };

        console.log('Asset Type created with data:', createdItem); // Debug log

        handleClose();

        loadData(); 
        toast.success(assetTypeForm.typeCatalogID ? "Asset type updated successfully" : "Asset type created successfully", {
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
        
        // Return success with data
        return { success: true, data: createdItem };
      } else {
        toast.error(response.message || "Failed to save asset type", {
          style: {
            fontFamily: '"DM Sans", sans-serif',
            fontSize: '15px',
          },
        });
        console.error("Error saving asset type:", response.message);
        return { success: false };
      }
    } catch (error) {
      toast.error("An error occurred while saving the asset type", {
        style: {
          fontFamily: '"DM Sans", sans-serif',
          fontSize: '15px',
        },
      });
      console.error("Error saving asset type:", error);
      return { success: false };
    } finally {
    }
  };

  const resetForm = () => {
    if(!hasTypeCatalogSavePermission) return;

    setAssetTypeForm({
      typeCatalogID: 0,
      entityType: "Asset",
      typeName: "",
      description: "",
      name: "",
      isActive: true
    });
    setErrors({});
  };

  return {
    assetTypeForm,
    errors,
    handleInputChange,
    handleSaveAssetType,
    resetForm
  };
}; 